import { computed, ref } from 'vue';
import type { SortingState } from '@tanstack/vue-table';
import type { FacetSelection } from '@/components/ui/data-table';
import { countChipKind, countChipLabel, farmAndDrugFacets, matchesSearch } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { applyFacetFilters, buildFacetOptions, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useMedicineInventory } from '@/plugins/store/actions/queries/inventory';
import type { MedicineInventoryRow } from '@/types';

/**
 * The Lager table's data and view state: the inventory query's every key, narrowed by the search box
 * and the farm/drug/latest-status header facets, and sorted by the table. The facet vocabulary is
 * built from every key rather than from what the filters leave of them, so narrowing one facet never
 * erases another's options.
 */
export function useInventoryList() {
    const { scope, isScopeEnabled } = useInventoryScope();
    const { data, isFetching, isError, refetch } = useMedicineInventory(scope, {
        enabled: isScopeEnabled,
    });

    const search = ref('');
    const columnFilters = ref<FacetSelection>({});
    // No sort until a column is chosen: the rows keep the server's farm and drug order, and the sort indicators stay off.
    const sorting = ref<SortingState>([]);

    const facets: RecordFacetDefinitions<MedicineInventoryRow> = {
        ...farmAndDrugFacets<MedicineInventoryRow>(),
        // Stemmer / Difference / Ingen status, on the column showing the latest count.
        last_count: { value: (row) => countChipKind(row.last_count), label: (row) => countChipLabel(countChipKind(row.last_count)) },
    };
    const rows = computed(() => data.value ?? []);
    const facetOptions = computed(() => buildFacetOptions(rows.value, facets));
    const visibleRows = computed(() => applyFacetFilters(rows.value, columnFilters.value, facets).filter((row) => matchesSearch(row, search.value)));

    return {
        columnFilters,
        facetOptions,
        isError,
        isFetching,
        refetch,
        rows,
        search,
        sorting,
        visibleRows,
    };
}
